import { Backup as BackupIcon, Cancel as CloseIcon } from '@mui/icons-material'
import {
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material'
import { type DropzoneInputProps, type DropzoneRootProps } from 'react-dropzone'
import { useTranslation } from 'react-i18next'

const BYTES_PER_MEGABYTE = 1024 * 1024

export interface FileFormFile {
  key?: string
  url?: string
  path?: string
  fileSize?: number
}

function getFileLabel(file: FileFormFile) {
  const fileSizeInMegabytes = (file.fileSize ?? 0) / BYTES_PER_MEGABYTE

  return `${file.path} - ${fileSizeInMegabytes.toFixed(2)} MB`
}

type GetRootProps = <T extends DropzoneRootProps>(props?: T) => T
type GetInputProps = <T extends DropzoneInputProps>(props?: T) => T

interface FileFormProps {
  acceptedFiles: FileFormFile[]
  getRootProps: GetRootProps
  getInputProps: GetInputProps
  removeItem: (key: string) => void
}

export default function FileForm({
  acceptedFiles,
  getRootProps,
  getInputProps,
  removeItem,
}: FileFormProps) {
  const { t } = useTranslation()

  return (
    <Stack
      component="section"
      direction="column"
      alignItems="center"
      minHeight="35vh"
    >
      <Stack
        {...getRootProps()}
        component="div"
        direction="column"
        alignItems="center"
        flex={1}
        width="100%"
        pt={10.625}
        pb={7.5}
        border={6}
        borderColor="divider"
        bgcolor="background.paper"
        color="text.secondary"
        borderRadius="2px"
        sx={{ borderStyle: 'dashed', outline: 'none' }}
      >
        <input {...getInputProps()} />
        <Typography variant="subtitle1">{t('views.uploadFiles')} </Typography>
        <BackupIcon color="primary" sx={{ fontSize: '15vh' }} />
      </Stack>
      <Stack component="aside" mt={3} width="100%">
        <List disablePadding>
          {acceptedFiles.map(file => (
            <ListItem
              key={file.key}
              disableGutters
              secondaryAction={
                <IconButton
                  aria-label={t('general.close')}
                  onClick={() => {
                    if (file.key !== undefined) removeItem(file.key)
                  }}
                  size="large"
                >
                  <CloseIcon color="primary" />
                </IconButton>
              }
            >
              <ListItemText
                primary={
                  file.url ? (
                    <Link
                      href={file.url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {getFileLabel(file)}
                    </Link>
                  ) : (
                    getFileLabel(file)
                  )
                }
              />
            </ListItem>
          ))}
        </List>
      </Stack>
    </Stack>
  )
}
